import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Text, Button, Title, Badge } from '@repletoryreach/sdk-ui';
import '../sku-rates.css';
import { TextInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDate, formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * The VAT switch. The platform is not registered yet, so it starts off: no
 * VAT is charged or printed. When it registers, the number and the date go
 * in and the switch goes on; from that date VAT is added on top of the
 * listed rates for tenants in the registration country. The rates stay
 * exclusive, so the platform's margin is unchanged; the tenant pays the tax.
 */
export function TaxCard() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'tax'], queryFn: () => admin.getTax() });
  const tax = q.data?.data;
  const [number, setNumber] = useState('');
  const [from, setFrom] = useState('');
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!tax || seeded) return;
    setNumber(tax.vat_registration_number ?? '');
    setFrom(tax.vat_effective_from ?? '');
    setSeeded(true);
  }, [tax, seeded]);

  const save = useMutation({
    mutationFn: (req: admin.UpdateTaxSettingsRequest) => admin.updateTax(req),
    onSuccess: (res) => {
      qc.setQueryData(['admin', 'tax'], res);
      setSeeded(false);
      notifySuccess(res.data.vat_enabled ? t('billing.tax.toast.on', { pct: res.data.vat_percent, from: res.data.vat_effective_from ? formatDate(res.data.vat_effective_from) : '' }) : t('billing.tax.toast.off'), t('billing.tax.title'));
    },
    onError: (e, req) => {
      // Prices include VAT: turning it on takes it out of every price, and a rate that would then fall
      // under its floor stops the switch. Offer to raise those rates to the floor, or leave VAT off.
      const err = isApiError(e) ? (e.body as { error?: { code?: string; count?: number; vat_percent?: number; rates?: Array<{ sku_code: string; amount_minor: number; floor_minor: number; currency: string }> } } | null)?.error : null;
      if (err?.code === 'RATES_UNDER_FLOOR_WITH_VAT') {
        openConfirmModal({
          title: t('billing.tax.underFloor.title', { count: err.count ?? 0 }),
          children: (
            <Stack gap="sm">
              <Text role="bodySmall">{t('billing.tax.underFloor.body', { count: err.count ?? 0, pct: err.vat_percent ?? 15 })}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {(err.rates ?? []).slice(0, 8).map((r) => t('billing.tax.underFloor.row', { sku: r.sku_code, now: formatMinor(r.amount_minor, r.currency), floor: formatMinor(r.floor_minor, r.currency) })).join(' · ')}
              </Text>
            </Stack>
          ),
          labels: { confirm: t('billing.tax.underFloor.raise'), cancel: t('billing.tax.underFloor.leave') },
          confirmProps: { color: 'orange' },
          onConfirm: () => save.mutate({ ...req, raise_rates_to_floor: true })
        });
        return;
      }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.tax.title'));
    }
  });

  function toggle(on: boolean) {
    if (!on) {
      openConfirmModal({
        title: t('billing.tax.confirm.offTitle'),
        children: <Text role="bodySmall">{t('billing.tax.confirm.offBody')}</Text>,
        labels: { confirm: t('billing.tax.confirm.off'), cancel: t('action.cancel') },
        confirmProps: { color: 'orange' },
        onConfirm: () => save.mutate({ vat_enabled: false })
      });
      return;
    }
    openConfirmModal({
      title: t('billing.tax.confirm.onTitle'),
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">{t('billing.tax.confirm.onBody', { pct: tax?.vat_percent ?? 15, country: tax?.vat_country_iso ?? 'ZA', example: formatMinor(1610, 'ZAR'), exampleVat: formatMinor(210, 'ZAR') })}</Text>
          <Text role="bodySmall">{t('billing.tax.confirm.onMargin')}</Text>
        </Stack>
      ),
      labels: { confirm: t('billing.tax.confirm.on'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => save.mutate({ vat_enabled: true, vat_registration_number: number, ...(from ? { vat_effective_from: from } : {}) })
    });
  }

  const needsNumber = Boolean(tax && !tax.vat_enabled && !number.trim());
  const dirty = Boolean(tax && (number !== (tax.vat_registration_number ?? '') || from !== (tax.vat_effective_from ?? '')));

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap="xs" style={{ minWidth: 0 }}>
            <Group gap="sm">
              <Title order={5}>{t('billing.tax.title')}</Title>
              {tax && (
                <Badge size="sm" variant="light" color={tax.vat_enabled ? 'green' : 'gray'}>
                  {tax.vat_enabled ? t('billing.tax.state.on', { pct: tax.vat_percent }) : t('billing.tax.state.off')}
                </Badge>
              )}
            </Group>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {tax?.vat_enabled
                ? t('billing.tax.onHint', { pct: tax.vat_percent, country: tax.vat_country_iso, from: tax.vat_effective_from ? formatDate(tax.vat_effective_from) : '' })
                : t('billing.tax.body')}
            </Text>
          </Stack>
          {tax && (
            <div style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Switch
                checked={tax.vat_enabled}
                onChange={toggle}
                label={t('billing.tax.switch')}
                // Never on without a registration number: the switch waits for one.
                disabled={save.isPending || needsNumber}
              />
            </div>
          )}
        </Group>
        <div className="rr-skurates-tax-row">
          <TextInput
            size="xs"
            style={{ width: 200 }}
            label={t('billing.tax.number')}
            value={number}
            onChange={(e) => setNumber(e.currentTarget.value)}
            placeholder="4123456789"
            description={needsNumber ? t('billing.tax.switchNeedsNumber') : t('billing.tax.numberHint', { country: tax?.vat_country_iso ?? 'ZA' })}
          />
          <TextInput
            size="xs"
            type="date"
            style={{ width: 170 }}
            label={t('billing.tax.from')}
            value={from}
            onChange={(e) => setFrom(e.currentTarget.value)}
            description={t('billing.tax.fromHint')}
          />
          <Button size="xs" variant="outline" loading={save.isPending} style={{ marginTop: 22 }}
            disabled={!tax || !dirty || (tax.vat_enabled && !number.trim())}
            onClick={() => save.mutate({ vat_registration_number: number, ...(from ? { vat_effective_from: from } : {}) })}>
            {t('action.save')}
          </Button>
        </div>
      </Stack>
    </Card>
  );
}
